/**
 * My Journeys Page
 *
 * View and manage user's created journeys.
 * Redesigned for maximum motivation and excitement!
 */

import { useLiveQuery } from "dexie-react-hooks";
import { useNavigate } from "react-router-dom";
import { db } from "@/db/database";
import type { Category } from "@/db/types";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Plus,
  Play,
  Edit,
  Trash2,
  Share2,
  CheckCircle2,
  Sparkles,
  Phone,
  Building2,
  MapPin,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import { getJourneyFlag } from "@/utils/quizMetadata";
import { BookMascot } from "@/components/ui/BookMascot";

// Category metadata with colors and icons
const CATEGORY_META: Record<
  Category,
  { color: string; gradient: string; icon: React.ReactNode; label: string }
> = {
  phone: {
    color: "from-blue-500 to-cyan-500",
    gradient: "bg-gradient-to-br from-blue-500/10 to-cyan-500/10",
    icon: <Phone className="w-5 h-5" />,
    label: "Phone Codes",
  },
  admin: {
    color: "from-purple-500 to-pink-500",
    gradient: "bg-gradient-to-br from-purple-500/10 to-pink-500/10",
    icon: <Building2 className="w-5 h-5" />,
    label: "Divisions",
  },
  cities: {
    color: "from-orange-500 to-red-500",
    gradient: "bg-gradient-to-br from-orange-500/10 to-red-500/10",
    icon: <MapPin className="w-5 h-5" />,
    label: "Cities",
  },
  custom: {
    color: "from-green-500 to-emerald-500",
    gradient: "bg-gradient-to-br from-green-500/10 to-emerald-500/10",
    icon: <Layers className="w-5 h-5" />,
    label: "Custom",
  },
};

export function MyJourneysPage() {
  const navigate = useNavigate();

  // Load user's journeys
  const journeys = useLiveQuery(() =>
    db.journeys.orderBy("lastModifiedAt").reverse().toArray()
  );

  const configs = useLiveQuery(() => db.quiz_configs.toArray());

  // Delete journey
  const handleDelete = async (journeyId: string, title: string) => {
    if (
      !confirm(
        `Are you sure you want to delete "${title}"?\n\nThis cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await db.journeys.delete(journeyId);
      toast.success("Journey deleted");
    } catch (error) {
      console.error("Error deleting journey:", error);
      toast.error("Failed to delete journey");
    }
  };

  // Calculate progress for a journey
  const getProgress = (journey: { quizzes: Array<{ isCompleted: boolean }> }) => {
    const completed = journey.quizzes.filter((q) => q.isCompleted).length;
    const total = journey.quizzes.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  if (!journeys || !configs) {
    return (
      <MainLayout title="My Journeys" subtitle="Loading...">
        <div className="container mx-auto p-8">
          <p className="text-center text-muted-foreground">Loading journeys...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      title="My Journeys"
      subtitle="Structured learning paths for mastering geography"
    >
      <div className="container mx-auto p-4 md:p-6 max-w-7xl">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-2">
              {journeys.length === 0
                ? "Start Your Learning Journey"
                : `${journeys.length} ${journeys.length === 1 ? "Journey" : "Journeys"}`}
            </h2>
            <p className="text-muted-foreground">
              {journeys.length === 0
                ? "Create your first structured quiz sequence"
                : "Continue your path to geographic mastery"}
            </p>
          </div>
          <Button size="lg" onClick={() => navigate("/journeys/builder")} className="w-full sm:w-auto">
            <Plus className="w-5 h-5 mr-2" />
            Create Journey
          </Button>
        </div>

        {/* Empty State */}
        {journeys.length === 0 && (
          <Card className="border-dashed border-2">
            <CardContent className="p-8 md:p-16 text-center">
              <BookMascot size="xl" className="mb-6" />
              <h3 className="text-2xl font-bold mb-3">Ready for an Adventure?</h3>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto text-lg">
                Journeys transform random quizzing into structured mastery. Pick
                a topic, follow the path, and watch your knowledge grow!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" onClick={() => navigate("/journeys/builder")} className="w-full sm:w-auto">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Journey
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/journeys/featured")}
                  className="w-full sm:w-auto"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Browse Featured
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Journey Grid */}
        {journeys.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {journeys.map((journey) => {
              const progress = getProgress(journey);
              const categoryMeta = journey.category
                ? CATEGORY_META[journey.category]
                : CATEGORY_META.custom;
              const isComplete = progress.completed === progress.total;

              return (
                <Card
                  key={journey.id}
                  className={`relative overflow-hidden border border-border/70 bg-card/80 shadow-lg shadow-primary/10 backdrop-blur hover:shadow-xl hover:shadow-primary/15 transition-all duration-300 cursor-pointer group ${
                    isComplete ? "border-green-500/50" : ""
                  }`}
                  onClick={() => navigate(`/journeys/${journey.id}`)}
                >
                  {/* Category Gradient Background */}
                  <div
                    className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity ${categoryMeta.gradient}`}
                  />

                  {/* Completion Badge */}
                  {isComplete && (
                    <div className="absolute top-4 right-4 z-10">
                      <div className="bg-green-500 text-white rounded-full p-2 shadow-lg">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                    </div>
                  )}

                  <CardHeader className="relative">
                    <div className="flex items-start gap-3 mb-3">
                      {/* Category Icon */}
                      <div
                        className={`bg-gradient-to-br ${categoryMeta.color} p-3 rounded-xl text-white shadow-lg`}
                      >
                        {categoryMeta.icon}
                      </div>

                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-xl mb-1 line-clamp-2">
                          <span className="mr-2">{getJourneyFlag(journey, configs)}</span>
                          {journey.title}
                        </CardTitle>
                        {journey.category && (
                          <span className="text-xs font-medium text-muted-foreground">
                            {categoryMeta.label}
                          </span>
                        )}
                      </div>
                    </div>

                    {journey.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                        {journey.description}
                      </p>
                    )}
                  </CardHeader>

                  <CardContent className="relative space-y-4">
                    {/* Circular Progress */}
                    <div className="flex items-center justify-center py-4">
                      <div className="relative">
                        <svg className="w-32 h-32 transform -rotate-90">
                          {/* Background circle */}
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="none"
                            className="text-muted/20"
                          />
                          {/* Progress circle */}
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke="url(#gradient-{journey.id})"
                            strokeWidth="8"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 56}`}
                            strokeDashoffset={`${
                              2 * Math.PI * 56 * (1 - progress.percentage / 100)
                            }`}
                            className="transition-all duration-500"
                          />
                          <defs>
                            <linearGradient
                              id={`gradient-${journey.id}`}
                              x1="0%"
                              y1="0%"
                              x2="100%"
                              y2="100%"
                            >
                              <stop
                                offset="0%"
                                className={isComplete ? "stop-color-green-500" : "stop-color-primary"}
                              />
                              <stop
                                offset="100%"
                                className={isComplete ? "stop-color-green-400" : "stop-color-primary/60"}
                              />
                            </linearGradient>
                          </defs>
                        </svg>
                        {/* Percentage Text */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <div className="text-3xl font-bold">
                            {progress.percentage}%
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {progress.completed}/{progress.total} done
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Layers className="w-4 h-4" />
                        <span>{journey.quizzes.length} quizzes</span>
                      </div>
                      {isComplete ? (
                        <div className="text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          Complete!
                        </div>
                      ) : (
                        <div className="text-primary font-medium">
                          {progress.total - progress.completed} remaining
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/journeys/${journey.id}`);
                        }}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        {progress.completed === 0
                          ? "Start"
                          : isComplete
                          ? "Review"
                          : "Continue"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/journeys/builder?edit=${journey.id}`);
                        }}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.info("Journey sharing coming soon!");
                        }}
                      >
                        <Share2 className="w-3 h-3 mr-1" />
                        Share
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(journey.id, journey.title);
                        }}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Motivation Card */}
        {journeys.length > 0 && (
          <Card className="mt-8 border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="text-4xl">🚀</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">
                    Keep the Momentum Going!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Every journey you complete brings you closer to geographic
                    mastery. Follow the sequences, embrace the learning process,
                    and celebrate each milestone. You've got this! 💪
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
